import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Leaf, Sparkles, X } from "lucide-react";

/**
 * Streak Milestone Celebration
 * 
 * A calm, elegant celebration when users hit streak milestones (7, 14, 30, 60, 90 days).
 * Designed to feel rewarding without being overwhelming - maintaining the Reclaim aesthetic.
 */

const MILESTONES = {
  7: {
    title: "One Week of Presence",
    message: "Seven days of showing up for yourself. This is the foundation of transformation.",
    icon: "leaf"
  },
  14: {
    title: "Two Weeks Strong",
    message: "Fourteen days of conscious practice. Your commitment is taking root.",
    icon: "sparkles"
  },
  30: {
    title: "One Month of Awakening",
    message: "Thirty days of presence. You're not just practicing—you're becoming.",
    icon: "leaf"
  },
  60: {
    title: "Two Months of Mastery",
    message: "Sixty days of dedication. Your practice has become a part of who you are.",
    icon: "sparkles"
  },
  90: {
    title: "A Season of Transformation",
    message: "Ninety days of continuous growth. You've created lasting change.",
    icon: "leaf"
  }
};

export const StreakMilestoneModal = ({ streakCount, onClose }) => {
  const [show, setShow] = useState(false);
  const milestone = MILESTONES[streakCount];

  useEffect(() => {
    if (milestone) {
      // Small delay for dramatic effect
      const timer = setTimeout(() => setShow(true), 300);
      return () => clearTimeout(timer);
    }
  }, [milestone]);

  const handleClose = () => {
    setShow(false);
    setTimeout(() => onClose?.(), 300);
  };

  if (!milestone) return null;

  const IconComponent = milestone.icon === "leaf" ? Leaf : Sparkles;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-6"
          onClick={handleClose}
        >
          {/* Backdrop with subtle glow */}
          <div className="absolute inset-0 bg-[#1A1D1A]/60 backdrop-blur-sm" />
          
          {/* Ambient glow effect */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <div className="w-96 h-96 bg-[#B8956C]/20 rounded-full blur-3xl" />
          </motion.div>

          {/* Modal Content */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-[#FAF9F7] rounded-3xl p-10 max-w-sm w-full text-center shadow-2xl"
            data-testid="streak-milestone-modal"
          >
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 text-[#9A9F9A] hover:text-[#4A4F4A] transition-colors"
              data-testid="milestone-close-btn"
            >
              <X size={20} />
            </button>

            {/* Animated Icon */}
            <motion.div
              initial={{ scale: 0, rotate: -20 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="relative mx-auto mb-8"
            >
              {/* Glow rings */}
              <div className="absolute inset-0 w-24 h-24 bg-[#B8956C]/30 rounded-full blur-xl animate-glow-pulse" />
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="absolute -inset-3 bg-gradient-to-br from-[#B8956C]/20 to-transparent rounded-full"
              />
              
              {/* Main icon container */}
              <div className="relative w-24 h-24 bg-gradient-to-br from-[#B8956C] to-[#9A7B56] rounded-full flex items-center justify-center shadow-lg">
                <IconComponent size={40} className="text-white" />
              </div>
            </motion.div>

            {/* Streak Number */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <span className="font-heading text-6xl text-[#2D4A2A]">{streakCount}</span>
              <span className="block text-[#7A7F7A] text-sm uppercase tracking-[0.2em] mt-1">
                Days
              </span>
            </motion.div>

            {/* Title */}
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="font-heading text-2xl text-[#1A1D1A] mt-6 mb-4"
            >
              {milestone.title}
            </motion.h2>

            {/* Message */}
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="text-[#4A4F4A] leading-relaxed"
            >
              {milestone.message}
            </motion.p>

            {/* Continue Button */}
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              onClick={handleClose}
              className="mt-8 w-full py-4 bg-[#2D4A2A] text-white rounded-full font-medium hover:bg-[#3D5A34] transition-colors"
              data-testid="milestone-continue-btn"
            >
              Continue
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * Hook to check if a streak count is a milestone
 */
export const useStreakMilestone = (currentStreak, previousStreak) => {
  const [showMilestone, setShowMilestone] = useState(false);
  const [milestoneStreak, setMilestoneStreak] = useState(null);

  useEffect(() => {
    // Check if we just crossed a milestone
    const milestones = [7, 14, 30, 60, 90];
    
    for (const milestone of milestones) {
      // User just reached this milestone (current >= milestone and previous < milestone)
      if (currentStreak >= milestone && (previousStreak === undefined || previousStreak < milestone)) {
        // Only trigger if they exactly hit the milestone or it's their first check
        if (currentStreak === milestone || (previousStreak !== undefined && previousStreak < milestone && currentStreak >= milestone)) {
          setMilestoneStreak(milestone);
          setShowMilestone(true);
          break;
        }
      }
    }
  }, [currentStreak, previousStreak]);

  const closeMilestone = () => {
    setShowMilestone(false);
    setMilestoneStreak(null);
  };

  return { showMilestone, milestoneStreak, closeMilestone };
};

export default StreakMilestoneModal;
